import type { FundingMethod } from './catalog';

/**
 * Static speed/fee profile per funding method. Used **only for ranking**
 * when computing recommendation badges (Best / Cheapest / Fastest / Slow).
 * The actual fee shown to the user always comes from the live quote — this
 * file is intentionally kept tiny and decoupled so we don't ship two
 * sources of truth for pricing.
 *
 * The numbers mirror `mock-quote.ts` so the badges stay consistent with
 * what the RecommendedCard displays. When the real backend pricing comes
 * online, this file will be replaced by per-corridor pricing pulled from
 * `claimLink().corridor.fundingMethods[].pricing`.
 */
export interface FundingProfile {
  /** Approximate USD fee — for ranking only. Live quote drives display. */
  feeApprox: number;
  /** Settlement time in hours for ranking. Instant = 0, 1–3 days ≈ 48. */
  speedHours: number;
}

export const FUNDING_PROFILES: Record<FundingMethod, FundingProfile> = {
  DEBIT_CARD: { feeApprox: 4.99, speedHours: 0 },
  CREDIT_CARD: { feeApprox: 9.99, speedHours: 0 },
  ACH: { feeApprox: 0, speedHours: 48 },
  RTP: { feeApprox: 1.99, speedHours: 0 },
  WALLET: { feeApprox: 1.99, speedHours: 0 },
  CASH_DEPOSIT: { feeApprox: 6.99, speedHours: 24 },
  BANK_BALANCE: { feeApprox: 0, speedHours: 0 },
};

export type FundingBadge = 'best' | 'cheapest' | 'fastest' | 'slow';

/** Multi-day methods earn a Slow tag — sub-day waits aren't worth flagging. */
const SLOW_THRESHOLD_HOURS = 24;

/** "Instant" tier for the Best heuristic (≤ 1h covers RTP, cards, wallet). */
const INSTANT_THRESHOLD_HOURS = 1;

const minBy = <T>(list: T[], key: (x: T) => number): T =>
  list.reduce((acc, cur) => (key(cur) < key(acc) ? cur : acc));

const maxBy = <T>(list: T[], key: (x: T) => number): T =>
  list.reduce((acc, cur) => (key(cur) > key(acc) ? cur : acc));

/**
 * Computes per-method recommendation badges from the corridor's available
 * funding list. At most ONE badge per method.
 *
 * Priority (a method only gets badged once, in this order):
 *   1. `best`     — cheapest among instant methods (or plain cheapest if
 *                   nothing is instant). The editorial pick.
 *   2. `cheapest` — lowest fee, when distinct from `best`.
 *   3. `fastest`  — strictly faster than `best`, when distinct from above.
 *   4. `slow`     — slowest method when settlement ≥ 24h AND there is a
 *                   meaningfully faster alternative.
 *
 * Returns `{}` when the corridor exposes fewer than two funding methods,
 * because the whole point of badging is to compare options.
 */
export const computeFundingBadges = (
  available: FundingMethod[],
): Partial<Record<FundingMethod, FundingBadge>> => {
  if (available.length < 2) return {};

  const profiles = available.map((method) => ({
    method,
    profile: FUNDING_PROFILES[method],
  }));

  const cheapest = minBy(profiles, (p) => p.profile.feeApprox);
  const fastest = minBy(profiles, (p) => p.profile.speedHours);
  const slowestCandidate = maxBy(profiles, (p) => p.profile.speedHours);

  const instantOnly = profiles.filter(
    (p) => p.profile.speedHours <= INSTANT_THRESHOLD_HOURS,
  );
  const best =
    instantOnly.length > 0
      ? minBy(instantOnly, (p) => p.profile.feeApprox)
      : minBy(profiles, (p) => p.profile.feeApprox);

  const badges: Partial<Record<FundingMethod, FundingBadge>> = {};
  badges[best.method] = 'best';

  if (cheapest.method !== best.method) {
    badges[cheapest.method] = 'cheapest';
  }
  if (
    fastest.method !== best.method &&
    fastest.method !== cheapest.method &&
    fastest.profile.speedHours < best.profile.speedHours
  ) {
    badges[fastest.method] = 'fastest';
  }
  if (
    !badges[slowestCandidate.method] &&
    slowestCandidate.profile.speedHours >= SLOW_THRESHOLD_HOURS &&
    slowestCandidate.profile.speedHours > fastest.profile.speedHours
  ) {
    badges[slowestCandidate.method] = 'slow';
  }

  return badges;
};
